/**
 * Student Survey Page (Bahasa Indonesia)
 *
 * Interface penilaian mesra kanak-kanak dengan:
 * - Sistem penilaian berasaskan emoji (😞 😐 🙂 😄)
 * - Reka bentuk berwarna dan ceria
 * - Bahasa mudah
 * - Soalan dari database
 * - Sasaran sentuhan besar untuk mudah alih
 * - Mesej privasi
 * - Fungsi auto-simpan
 * - Student's school is automatically loaded from profile
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentService, schoolService } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useAutoSave } from '../hooks';

const StudentSurvey = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // School info (loaded from student's profile)
  const [selectedSchool, setSelectedSchool] = useState(null);

  // Emoji rating options
  const ratingOptions = [
    { value: 1, emoji: "😞", label: "Tidak", color: "bg-red-100 hover:bg-red-200 border-red-300" },
    { value: 2, emoji: "😐", label: "Kurang", color: "bg-orange-100 hover:bg-orange-200 border-orange-300" },
    { value: 3, emoji: "🙂", label: "Ya", color: "bg-yellow-100 hover:bg-yellow-200 border-yellow-300" },
    { value: 4, emoji: "😄", label: "Sangat!", color: "bg-green-100 hover:bg-green-200 border-green-300" }
  ];

  const storageKey = `student_survey_${profile?.id || 'anonymous'}`;

  // Auto-save to localStorage
  const { isSaving } = useAutoSave(storageKey, {
    responses,
    currentIndex,
    sessionId,
  }, 500);

  // Initialize survey - load school from profile, restore saved data, and then questions
  useEffect(() => {
    initializeSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeSurvey = async () => {
    setLoading(true);

    // First, restore saved data from localStorage synchronously
    let restoredSessionId = null;
    try {
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.responses) setResponses(parsed.responses);
        if (parsed.currentIndex !== undefined) setCurrentIndex(parsed.currentIndex);
        if (parsed.sessionId) restoredSessionId = parsed.sessionId;
      }
    } catch (e) {
      console.error('Error parsing saved data:', e);
      localStorage.removeItem(storageKey);
    }

    try {
      // Check if student has a school assigned
      if (!profile?.school_id) {
        console.error('Student has no school assigned');
        setLoading(false);
        return;
      }

      // Load school info from student's profile
      const { school, error: schoolError } = await schoolService.getSchool(profile.school_id);
      if (schoolError) {
        console.error('Error loading school:', schoolError);
      } else {
        setSelectedSchool(school);
      }

      // Load questions
      const { questions: data, error } = await assessmentService.getQuestions('student');

      if (error) {
        console.error('Error loading questions:', error);
        setQuestions([]);
      } else {
        setQuestions(data || []);
      }

      // Create session if not exists and we have questions
      const questionsData = data || [];
      if (!restoredSessionId && questionsData.length > 0) {
        const { session, error: sessionError } = await assessmentService.createSession({
          user_id: profile?.id,
          total_questions: questionsData.length,
          is_anonymous: false,
          target_school_id: profile.school_id, // Use student's assigned school
          auto_save_data: { role: 'student' }
        });

        if (!sessionError && session) {
          setSessionId(session.id);
        }
      } else if (restoredSessionId) {
        setSessionId(restoredSessionId);
      }
    } catch (error) {
      console.error('Error initializing survey:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRating = async (rating) => {
    const question = questions[currentIndex];

    // Save response
    const newResponses = {
      ...responses,
      [question.id]: {
        question_id: question.id,
        response_value: { rating },
        question_text: question.question_text
      }
    };

    setResponses(newResponses);

    // Auto-advance after short delay
    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }, 300);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(responses).length === 0) {
      alert('Silakan jawab minimal satu pertanyaan sebelum mengirim!');
      return;
    }

    setSubmitting(true);

    try {
      // Submit responses with proper question_id (UUID from database)
      const responsesToSubmit = Object.values(responses).map(r => ({
        session_id: sessionId,
        question_id: r.question_id,
        user_id: profile?.id,
        response_value: r.response_value
      }));

      await assessmentService.submitResponses(responsesToSubmit);
      await assessmentService.completeSession(sessionId);

      // Clear saved data
      localStorage.removeItem(storageKey);

      // Show success message
      setShowSuccess(true);

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('Oops! Ada masalah. Jawaban anda sudah disimpan dan anda bisa coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle student without school assigned
  if (!profile?.school_id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md">
          <div className="text-6xl mb-4">🏫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Sekolah Belum Ditentukan</h2>
          <p className="text-gray-600 mb-4">
            Akun kamu belum terdaftar di sekolah manapun. Silakan hubungi administrator atau guru kamu.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600"
          >
            Kembali ke Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📝</div>
          <p className="text-xl text-gray-700 font-medium">Memuat survei anda...</p>
        </div>
      </div>
    );
  }

  // Handle no questions
  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md">
          <div className="text-6xl mb-4">📭</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Belum Ada Pertanyaan</h2>
          <p className="text-gray-600 mb-4">
            Survei untuk siswa belum tersedia. Silakan hubungi administrator.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600"
          >
            Kembali ke Beranda
          </button>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100">
        <div className="text-center animate-bounce">
          <div className="text-9xl mb-6">🎉</div>
          <h2 className="text-4xl font-bold text-purple-600 mb-4">Hebat!</h2>
          <p className="text-2xl text-gray-700">Terima kasih sudah berbagi pendapat anda!</p>
          <p className="text-lg text-gray-600 mt-4">Anda membantu membuat sekolah kita lebih baik! ⭐</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;

  // Get emoji from question metadata or use default
  const questionEmoji = currentQuestion.metadata?.emoji || "📝";

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 p-4">
      {/* School Info Banner */}
      {selectedSchool && (
        <div className="bg-purple-600 text-white py-2 px-4 rounded-xl mb-4 max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="font-medium">{selectedSchool.nama}</span>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">📝</div>
          <h1 className="text-3xl md:text-4xl font-bold text-purple-600 mb-2">
            Ceritakan Pendapat Kamu!
          </h1>
          <p className="text-lg text-gray-700">
            Pendapat kamu membantu membuat sekolah lebih baik! 🌟
          </p>
        </div>

        {/* Privacy Message */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 mb-6 text-center">
          <p className="text-sm text-blue-800 font-medium">
            🔒 Jawaban kamu rahasia dan aman. Jujur dan bersenang-senanglah!
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-lg font-bold text-purple-600">
              Pertanyaan {currentIndex + 1} dari {questions.length}
            </span>
            <span className="text-lg font-bold text-purple-600">
              {Math.round(progress)}% Selesai
            </span>
          </div>
          <div className="h-4 bg-white rounded-full overflow-hidden border-2 border-purple-300">
            <div
              className="h-full bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6 transform transition-all duration-300 hover:scale-105">
          <div className="text-center">
            <div className="text-7xl mb-6">{questionEmoji}</div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 leading-relaxed">
              {currentQuestion.question_text}
            </h2>

            {/* Emoji Ratings */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {ratingOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleRating(option.value)}
                  className={`
                    ${option.color}
                    border-4 rounded-2xl p-6 transition-all duration-200
                    transform hover:scale-110 active:scale-95
                    ${responses[currentQuestion.id]?.response_value?.rating === option.value
                      ? 'ring-4 ring-purple-400 scale-105'
                      : ''
                    }
                  `}
                >
                  <div className="text-5xl md:text-6xl mb-2">{option.emoji}</div>
                  <div className="text-sm md:text-base font-bold text-gray-700">
                    {option.label}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="px-6 py-4 bg-white text-purple-600 rounded-2xl font-bold text-lg border-4 border-purple-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-purple-50 transition-all shadow-lg"
          >
            ← Kembali
          </button>

          {!isLastQuestion ? (
            <button
              onClick={handleNext}
              disabled={!responses[currentQuestion.id]}
              className="px-6 py-4 bg-purple-500 text-white rounded-2xl font-bold text-lg border-4 border-purple-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-purple-600 transition-all shadow-lg flex-1"
            >
              Selanjutnya →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(responses).length === 0}
              className="px-6 py-4 bg-green-500 text-white rounded-2xl font-bold text-lg border-4 border-green-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-green-600 transition-all shadow-lg flex-1"
            >
              {submitting ? '⏳ Mengirim...' : '✓ Selesai!'}
            </button>
          )}
        </div>

        {/* Auto-save indicator */}
        {isSaving && (
          <p className="text-center mt-4 text-sm text-purple-600 font-medium">
            💾 Menyimpan jawaban anda...
          </p>
        )}

        {/* Question dots */}
        <div className="flex justify-center gap-2 mt-6 flex-wrap">
          {questions.map((q, idx) => (
            <button
              key={q.id}
              onClick={() => setCurrentIndex(idx)}
              className={`
                w-3 h-3 md:w-4 md:h-4 rounded-full transition-all
                ${idx === currentIndex
                  ? 'bg-purple-600 scale-125'
                  : responses[q.id]
                  ? 'bg-green-400'
                  : 'bg-gray-300'
                }
              `}
              aria-label={`Go to question ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default StudentSurvey;
